/**
 * Pure functions of the `spelling_bee` type: the progress blob, the submission, adding a word
 * and folding the server's verdict back in. No component here, so `module.ts` stays light in
 * the eager bundle.
 */
import type { Verdict } from "../types";
import { normalizeWord, points, wordIssue, type Hive } from "./hive";
import {
  MAX_WORD_LENGTH,
  MAX_WORDS,
  type SpellingBeeDefinition,
  type SpellingBeeProgress,
  type SpellingBeeSubmission,
  type WordIssue,
} from "./types";

/** Same text as `DEFAULT_RULES` in `backend/apps/puzzletypes/spelling_bee/type.py`. */
const DEFAULT_RULES = {
  en: "Make words of at least four letters from the seven letters of the hive. Every word must use the centre letter; letters may be used more than once. A four-letter word scores 1 point, a longer word one point per letter, and a word using all seven letters earns 7 more. Build your list, then submit it once: you are ranked on the points of the words in the dictionary. A list with no valid word does not count.",
  fr: "Formez des mots d'au moins quatre lettres avec les sept lettres de la ruche. Chaque mot doit contenir la lettre centrale ; une lettre peut servir plusieurs fois. Un mot de quatre lettres vaut 1 point, un mot plus long un point par lettre, et un mot qui utilise les sept lettres rapporte 7 points de plus. Composez votre liste, puis soumettez-la une seule fois : vous êtes classé sur les points des mots du dictionnaire. Une liste sans aucun mot valable ne compte pas.",
};

export function emptyDefinition(): SpellingBeeDefinition {
  return {
    puzzleType: "spelling_bee",
    typeVersion: 1,
    language: "en",
    letters: "CKLOPTU",
    centre: "T",
    rulesText: { ...DEFAULT_RULES },
  };
}

export function emptyProgress(_definition: SpellingBeeDefinition): SpellingBeeProgress {
  return { version: 1, words: [] };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const STORED_WORD = /^[A-Z]{1,32}$/;

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(
  definition: SpellingBeeDefinition,
  raw: unknown,
): SpellingBeeProgress {
  if (!isRecord(raw) || !Array.isArray(raw.words)) return emptyProgress(definition);
  const words: string[] = [];
  for (const item of raw.words) {
    if (typeof item !== "string") continue;
    const word = normalizeWord(item);
    if (STORED_WORD.test(word) && !words.includes(word)) words.push(word);
    if (words.length >= MAX_WORDS) break;
  }
  const rejected: Record<string, string> = {};
  if (isRecord(raw.rejected)) {
    for (const [word, code] of Object.entries(raw.rejected)) {
      if (typeof code === "string" && words.includes(word)) rejected[word] = code;
    }
  }
  return {
    version: 1,
    words,
    ...(Object.keys(rejected).length > 0 ? { rejected } : {}),
  };
}

export function serializeSubmission(
  _definition: SpellingBeeDefinition,
  progress: SpellingBeeProgress,
): SpellingBeeSubmission {
  return { words: [...progress.words] };
}

/** What happened to a word the player tried to add. */
export type AddResult =
  | { kind: "added"; word: string; progress: SpellingBeeProgress }
  | { kind: "issue"; word: string; issue: WordIssue }
  | { kind: "duplicate"; word: string }
  | { kind: "full"; word: string }
  | { kind: "empty" };

/**
 * Adds a word to the list when it keeps every rule the player can see. Whether the
 * dictionary knows it is the server's call, at submission.
 */
export function addWord(hive: Hive, progress: SpellingBeeProgress, raw: string): AddResult {
  const word = normalizeWord(raw);
  if (!word) return { kind: "empty" };
  if (word.length > MAX_WORD_LENGTH) return { kind: "issue", word, issue: "letter_not_allowed" };
  const issue = wordIssue(hive, word);
  if (issue) return { kind: "issue", word, issue };
  if (progress.words.includes(word)) return { kind: "duplicate", word };
  if (progress.words.length >= MAX_WORDS) return { kind: "full", word };
  return { kind: "added", word, progress: { ...progress, words: [...progress.words, word] } };
}

/** Drops a word, and the server's mark on it with it. */
export function removeWord(progress: SpellingBeeProgress, word: string): SpellingBeeProgress {
  const words = progress.words.filter((w) => w !== word);
  if (!progress.rejected || !(word in progress.rejected)) return { ...progress, words };
  const rejected = { ...progress.rejected };
  delete rejected[word];
  return {
    version: 1,
    words,
    ...(Object.keys(rejected).length > 0 ? { rejected } : {}),
  };
}

/**
 * Folds one verdict into the progress: every word the server refused is marked with the
 * reason it gave. The verdict judged the whole list, so its marks replace the previous
 * ones. A repeated word is not marked: the list never holds one twice. The feedback only
 * ever names the player's own words (the server never names a word they missed).
 */
export function applyVerdict(
  _definition: SpellingBeeDefinition,
  progress: SpellingBeeProgress,
  verdict: Verdict<SpellingBeeSubmission>,
): SpellingBeeProgress {
  const rejected: Record<string, string> = {};
  for (const item of verdict.feedback) {
    const word = item.params?.word;
    if (item.code === "duplicate_word" || typeof word !== "string") continue;
    if (progress.words.includes(word)) rejected[word] = item.code;
  }
  return {
    version: 1,
    words: progress.words,
    ...(Object.keys(rejected).length > 0 ? { rejected } : {}),
  };
}

/** The points the list would score if the dictionary knew every word not already refused. */
export function potentialScore(hive: Hive, progress: SpellingBeeProgress): number {
  return progress.words
    .filter((word) => !progress.rejected?.[word] && wordIssue(hive, word) === null)
    .reduce((sum, word) => sum + points(hive, word), 0);
}
